import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { UserProfile, DailyStreak } from '@/types';

const getDefaultStreak = (): DailyStreak => {
  const today = new Date();
  return {
    currentStreak: 0,
    longestStreak: 0,
    lastLoginDate: today,
    streakHistory: [],
    rewards: []
  };
};

export function useUserProfile() {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setIsLoading(true);
        
        // Get authenticated user
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          setProfile(null);
          setIsLoading(false);
          return;
        }

        // Fetch profile from database
        const { data, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (profileError) {
          throw profileError;
        }

        if (data) {
          // Map database fields to UserProfile type
          const userProfile: UserProfile = {
            userId: data.id,
            name: data.display_name || 'User',
            displayName: data.display_name || 'user',
            bio: data.bio || '',
            role: 'Musician',
            location: '',
            avatarUrl: data.avatar_url || 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d',
            genres: [],
            socialLinks: {
              spotify: '',
              instagram: '',
              youtube: '',
              website: ''
            },
            memberSince: new Date(data.created_at || Date.now()),
            totalReleases: 0,
            totalStreams: 0,
            followers: 0,
            following: 0,
            xp: 0,
            level: 1,
            dailyStreak: getDefaultStreak()
          };

          setProfile(userProfile);
        }
      } catch (err) {
        setError(err as Error);
        console.error('Error fetching profile:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();

    // Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      fetchProfile();
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return { profile, isLoading, error };
}
